import React, { useMemo } from "react";
import TableCell from "../../common/ui/TableCell";
import TinyMCEpreview from "../../const/TinyMCEpreview";

export default function IncidentView({ doc }) {
  const values = doc?.meta || {};
  // const dns = doc?.dns;

  // // 상단
  // const txnDate = values.doc_date || "-";
  // const rawTitle = String(doc?.title || "").trim();
  // const title =
  //   (rawTitle && rawTitle !== "(제목 없음)" ? rawTitle : "") ||
  //   autoTitle(values.incident_type, values.location, values.incident_date) ||
  //   "(제목 없음)";

  // 메타
  const incidentNo = values.incident_no || "-";
  const incidentDate = values.incident_date || "-";
  const location = values.location || "-";
  const type = values.incident_type || "-";
  const severity = values.severity || "MODERATE";
  const severityNote = values.severity_note || "-";
  const reporter = values.reporter || "-";
  const witnesses = normalizeWitnesses(values.witnesses);

  const minor = nz(values.minor_injury_count);
  const major = nz(values.major_injury_count);
  const fatal = nz(values.fatality_count);
  const propDamage = nz(values.property_damage_cost);
  const propDamageNote = values.property_damage_note || "-";

  const description = values.description || "-";
  const cause = values.cause || "-";
  const corrective = values.corrective_action || "-";
  const preventive = values.preventive_action || "-";
  const note = values.note || "-";

  // 열람 권한(있으면 표시) — 작성 화면의 "열람 권한"과 매칭
  const referenceUsers = useMemo(() => {
    if (values.reference_users === "ALL") return "전체(ALL)";

    const details = Array.isArray(values.reference_user_details)
      ? values.reference_user_details
      : Array.isArray(values.referenceUserDetails)
      ? values.referenceUserDetails
      : [];
    if (details.length) {
      return details.map(formatRefUser).filter(Boolean).join(", ");
    }

    const ids = Array.isArray(values.reference_users)
      ? values.reference_users
      : [];
    return ids.length ? ids.join(", ") : "-";
  }, [
    values.reference_users,
    values.reference_user_details,
    values.referenceUserDetails,
  ]);

  // 사진: values.photos 또는 서버 메타
  const photos = useMemo(() => {
    const fromValues = Array.isArray(values.photos) ? values.photos : [];
    const fromExtra = Array.isArray(doc?.extra?.photos_meta)
      ? doc.extra.photos_meta.map((m) => ({ name: m.name, url: m.url }))
      : [];
    return fromValues.length ? fromValues : fromExtra;
  }, [values.photos, doc?.extra?.photos_meta]);

  const section = "pt-3";
  const heading = "text-[13px] font-semibold mb-2";
  const sectionStyle = {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  };
  const headingStyle = {
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "0.5rem",
  };

  return (
    <div className="space-y-4 text-[13px] leading-tight">
      {/* 기본 정보 (공통 필드는 FormViewManager에서 표시됨) */}
      {/* <div className={heading}>기본 정보</div> */}
      <table className="w-full border border-spacing-y-1">
        <tbody>
          <tr>
            <TableCell
              label="비고"
              value={<TinyMCEpreview html={note} />}
              compact
            />
          </tr>
        </tbody>
      </table>

      {/* 사고 상세 정보 */}
      <section className={section}>
        <div className={heading}>사고 상세 정보</div>
        <table className="w-full border border-spacing-y-1">
          <tbody>
            <tr>
              <TableCell label="사고 번호" value={incidentNo} compact />
            </tr>
            <tr>
              <TableCell
                label="사고 일자"
                value={fmtDate(incidentDate)}
                compact
              />
            </tr>
            <tr>
              <TableCell label="장소" value={location} compact />
            </tr>
            <tr>
              <TableCell label="사고 유형" value={type} compact />
            </tr>
            <tr>
              <TableCell label="중대도" value={severity} compact />
            </tr>
            <tr>
              <TableCell label="중대도 메모" value={severityNote} compact />
            </tr>
            <tr>
              <TableCell label="신고자*" value={reporter} compact />
            </tr>
            <tr>
              <TableCell
                label="목격자"
                value={witnesses.length ? witnesses.join(", ") : "-"}
                compact
              />
            </tr>
          </tbody>
        </table>
      </section>

      {/* 피해/비용 */}
      <section className={section}>
        <div className={heading}>피해 / 비용</div>
        <table className="w-full border border-spacing-y-1">
          <tbody>
            <tr>
              <TableCell label="경상자 수" value={minor} compact />
            </tr>
            <tr>
              <TableCell label="중상자 수" value={major} compact />
            </tr>
            <tr>
              <TableCell label="사망자 수" value={fatal} compact />
            </tr>
            <tr>
              <TableCell
                label="재산 피해액(원)"
                value={Number(propDamage).toLocaleString?.() ?? propDamage}
                compact
              />
            </tr>
            <tr>
              <TableCell
                label="재산 피해 메모"
                value={propDamageNote}
                compact
              />
            </tr>
          </tbody>
        </table>
        <div className="mt-1 text-[12px] text-gray-500">
          인명 피해 합계:{" "}
          {Number(minor || 0) + Number(major || 0) + Number(fatal || 0)}명
        </div>
      </section>

      {/* 설명 / 원인 / 조치 */}
      <section className={section}>
        <div className={heading}>사고 설명 / 원인 / 조치</div>
        <table className="w-full border border-spacing-y-1">
          <tbody>
            <tr>
              <TableCell label="사고 개요*" value={description} compact />
            </tr>
            <tr>
              <TableCell label="원인 분석" value={cause} compact />
            </tr>
            <tr>
              <TableCell label="즉시 시정 조치" value={corrective} compact />
            </tr>
            <tr>
              <TableCell label="재발 방지 대책" value={preventive} compact />
            </tr>
          </tbody>
        </table>
      </section>

      {/* 열람 권한 */}
      <section className={section}>
        <div className={heading}>열람 권한</div>
        <table className="w-full border border-spacing-y-1">
          <tbody>
            <tr>
              <TableCell label="열람 사용자" value={referenceUsers} compact />
            </tr>
          </tbody>
        </table>
      </section>

      {/* 사진 미리보기 */}
      {Array.isArray(photos) && photos.length > 0 && (
        <section className={section} style={sectionStyle}>
          <div className={heading} style={headingStyle}>
            사진
          </div>
          <div
            className="grid grid-cols-2 gap-2 min-w-0"
            style={{
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0.5rem",
            }}
          >
            {photos.map((p, idx) => {
              let src = null;
              if (p.url) src = p.url;
              else if (p.file instanceof File || p.file instanceof Blob)
                src = URL.createObjectURL(p.file);
              const name = p.name || `photo_${idx + 1}`;
              return (
                <div
                  key={idx}
                  className="relative w-full border rounded-lg overflow-hidden bg-gray-100 flex flex-col items-center min-w-0"
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    height: "8rem",
                  }}
                >
                  <div
                    className="flex-1 flex items-center justify-center overflow-hidden w-full min-w-0"
                    style={{
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={name}
                        className="max-h-full max-w-full object-contain min-w-0"
                        style={{
                          maxHeight: "100%",
                          maxWidth: "100%",
                          boxSizing: "border-box",
                        }}
                      />
                    ) : (
                      <span
                        className="text-xs text-gray-400"
                        style={{ fontSize: "0.75rem" }}
                      >
                        이미지 없음
                      </span>
                    )}
                  </div>
                  <div
                    className="w-full text-center text-xs text-gray-700 truncate px-1 py-1 min-w-0"
                    style={{
                      fontSize: "0.75rem",
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {name}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* 첨부파일 */}
      {/* {attachment?.url && (
        <section className={section}>
          <div className={heading}>첨부</div>
          {isPdf ? (
            <PdfPreview fileUrl={attachment.url} />
          ) : (
            <div className="text-[13px] text-gray-600">
              미리보기를 지원하지 않는 형식입니다.{" "}
              <a
                href={getTxnDownloadUrl(dns)}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                다운로드
              </a>
            </div>
          )}
        </section>
      )} */}
    </div>
  );
}

/* ---------------- helpers ---------------- */

function formatRefUser(u) {
  if (!u) return "";
  const name = u.name || u.username || u.email || u.id;
  const company = u.company_name || u.company || "";
  const dept = u.department_name || u.department || "";
  const pos = u.position_name || u.position || "";
  const extra = [company, dept, pos].filter(Boolean).join(" / ");
  return extra ? `${name} (${extra})` : String(name || "");
}

function normalizeWitnesses(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((w) => (typeof w === "string" ? w : w?.name || ""))
    .filter(Boolean);
}

function nz(v) {
  if (v === "" || v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtDate(d) {
  if (!d) return "-";
  if (typeof d === "string") return d.slice(0, 10);
  try {
    return new Date(d).toISOString().slice(0, 10);
  } catch {
    return String(d);
  }
}
